import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Timestamp } from '@angular/fire/firestore';
import { TimeoutError } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { OpenSquashService } from '../shared/services/open-squash-service';
import { MatchExtended, MatchState } from '../shared/model/match';
import { RefereeState } from './models/referee-state';
import { RefereeEvent, EventType, ServeBox } from './models/referee-event';
import { GameDetail } from '../shared/model/game-detail';
import { Game } from '../shared/model/game';
import { GameEndConfirmationDialogComponent } from './game-end-confirmation-dialog/game-end-confirmation-dialog.component';
import { PlayerColorPickerDialogComponent } from './player-color-picker-dialog/player-color-picker-dialog.component';

@Component({
  selector: 'app-match-referee',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './match-referee.component.html',
  styleUrls: ['./match-referee.component.scss']
})
export class MatchRefereeComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(OpenSquashService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  match: MatchExtended | null = null;
  localState!: RefereeState;

  // Flag para bloquear acciones mientras se espera confirmación de fin de juego
  private isWaitingForGameEndConfirmation = false;

  // Timers
  private timerInterval: any;

  // Computed properties para UI
  currentGameDetail: GameDetail | null = null;
  matchElapsedTime = '00:00';
  gameElapsedTime = '00:00';
  warmupProgress = 0;
  restProgress = 0;
  timeoutElapsedTime = '00:00';

  player1ShortName = '';
  player2ShortName = '';
  player1ColorBg = '';
  player2ColorBg = '';
  player1ColorBorder = '';
  player2ColorBorder = '';

  // Random animation state
  showingRandomAnimation = false;
  randomAnimationResult: 1 | 2 | null = null;

  // Theme state
  isDarkTheme = true;

  // Match saved state
  matchSavedSuccessfully = false;

  // Sync error state (fallo al guardar el partido finalizado)
  syncError = false;
  isSyncing = false;
  private pendingFinalizeData: { winner: 1 | 2; durationSeconds: number } | null = null;

  // Error state
  showErrorState = false;
  errorMessage = '';
  errorTitle = '';

  // Screen Wake Lock (evita que la pantalla se bloquee durante el partido)
  private wakeLock: any = null;
  private visibilityChangeHandler = () => this.handleVisibilityChange();

  ngOnInit(): void {
    this.service.setTournament('1open-femenino');

    // Detectar preferencia de tema del navegador
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.isDarkTheme = prefersDark;

    // Prevenir scroll del body en móviles
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.documentElement.style.overflow = 'hidden';

    this.requestWakeLock();
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    const matchCode = this.route.snapshot.paramMap.get('matchCode');
    if (!matchCode) {
      this.showError('Código de partido no especificado');
      this.router.navigate(['/']);
      return;
    }

    this.loadMatch(matchCode);
  }

  private getLocalStorageKey(matchCode: string): string {
    return `referee_state_${matchCode}`;
  }

  private saveStateToLocalStorage(matchCode: string): void {
    try {
      // Convertir Timestamps a milisegundos antes de guardar
      const localStateSerialized = JSON.parse(JSON.stringify(this.localState, (key, value) => {
        // Convertir Timestamp a milisegundos
        if (value && typeof value === 'object' && value.seconds !== undefined && value.nanoseconds !== undefined) {
          return { _timestamp: value.seconds * 1000 + value.nanoseconds / 1000000 };
        }
        return value;
      }));

      const state = {
        localState: localStateSerialized,
        matchElapsedTime: this.matchElapsedTime,
        gameElapsedTime: this.gameElapsedTime,
        player1ShortName: this.player1ShortName,
        player2ShortName: this.player2ShortName,
        player1ColorBg: this.player1ColorBg,
        player2ColorBg: this.player2ColorBg,
        player1ColorBorder: this.player1ColorBorder,
        player2ColorBorder: this.player2ColorBorder,
        savedAt: Date.now()
      };
      localStorage.setItem(this.getLocalStorageKey(matchCode), JSON.stringify(state));
    } catch (err) {
      console.error('Error guardando estado en localStorage:', err);
    }
  }

  private loadStateFromLocalStorage(matchCode: string): boolean {
    try {
      const saved = localStorage.getItem(this.getLocalStorageKey(matchCode));
      if (!saved) return false;

      const state = JSON.parse(saved);

      // Verificar que no sea un estado muy antiguo (más de 24 horas)
      const hoursSinceSaved = (Date.now() - state.savedAt) / (1000 * 60 * 60);
      if (hoursSinceSaved > 24) {
        this.clearStateFromLocalStorage(matchCode);
        return false;
      }

      // Convertir de vuelta los timestamps
      const localStateDeserialized = JSON.parse(JSON.stringify(state.localState), (_key, value) => {
        // Convertir milisegundos de vuelta a Timestamp
        if (value && typeof value === 'object' && value._timestamp !== undefined) {
          return Timestamp.fromMillis(value._timestamp);
        }
        return value;
      });

      this.localState = localStateDeserialized;
      this.matchElapsedTime = state.matchElapsedTime;
      this.gameElapsedTime = state.gameElapsedTime;
      this.player1ShortName = state.player1ShortName;
      this.player2ShortName = state.player2ShortName;
      this.player1ColorBg = state.player1ColorBg;
      this.player2ColorBg = state.player2ColorBg;
      this.player1ColorBorder = state.player1ColorBorder;
      this.player2ColorBorder = state.player2ColorBorder;

      this.currentGameDetail = this.localState.games[this.localState.currentGame];

      return true;
    } catch (err) {
      console.error('Error cargando estado desde localStorage:', err);
      return false;
    }
  }

  private clearStateFromLocalStorage(matchCode: string): void {
    try {
      localStorage.removeItem(this.getLocalStorageKey(matchCode));
    } catch (err) {
      console.error('Error limpiando localStorage:', err);
    }
  }

  ngOnDestroy(): void {
    // Restaurar scroll del body
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.documentElement.style.overflow = '';

    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    this.releaseWakeLock();

    this.clearIntervals();
  }

  private async requestWakeLock(): Promise<void> {
    try {
      const nav = navigator as any;
      if ('wakeLock' in nav) {
        this.wakeLock = await nav.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null;
        });
      }
    } catch (err) {
      console.warn('No se pudo activar wake lock:', err);
    }
  }

  private async releaseWakeLock(): Promise<void> {
    try {
      if (this.wakeLock) {
        await this.wakeLock.release();
        this.wakeLock = null;
      }
    } catch (err) {
      console.warn('Error liberando wake lock:', err);
    }
  }

  private handleVisibilityChange(): void {
    // El navegador libera el wake lock automáticamente cuando la página se oculta.
    // Al volver a ser visible, lo re-adquirimos si el partido sigue activo.
    if (document.visibilityState === 'visible' && !this.wakeLock) {
      if (!this.localState || this.localState.phase !== 'FINISHED') {
        this.requestWakeLock();
      }
    }
  }

  private loadMatch(code: string): void {
    this.service.getMatchByCode(code).subscribe({
      next: (match) => {
        if (!match) {
          this.showError('Partido no encontrado');
          this.router.navigate(['/']);
          return;
        }

        this.match = match;

        // Validar estado del partido
        if (match.state === MatchState.FINALIZADO) {
          // Mostrar pantalla de resumen del partido finalizado
          this.loadFinishedMatch();
          return;
        }

        if (match.state === MatchState.JUGANDO) {
          // Intentar recuperar el estado desde localStorage
          const recovered = this.loadStateFromLocalStorage(code);

          if (recovered) {
            this.showNotification('Partido recuperado - puedes continuar arbitrando');
            this.startTimers();
            return;
          } else {
            // No se pudo recuperar: mostrar pantalla de error
            this.showErrorState = true;
            this.errorTitle = 'Partido en Curso';
            this.errorMessage = 'Este partido ya está siendo arbitrado. No es posible iniciar una nueva sesión de arbitraje.';
            return;
          }
        }

        if (match.state !== MatchState.PROGRAMADO && match.state !== MatchState.EN_CANCHA) {
          this.showError('El partido no está programado para jugarse');
          this.router.navigate(['/']);
          return;
        }

        this.initializeLocalState();
        this.startTimers();
      },
      error: (err) => {
        console.error('Error cargando partido:', err);
        this.showError('Error al cargar el partido');
        this.router.navigate(['/']);
      }
    });
  }

  private loadFinishedMatch(): void {
    if (!this.match) return;

    // Colores por defecto
    const player1Color = '#14b8a6';
    const player2Color = '#FF8C42';

    // Calcular colores derivados
    this.player1ColorBg = this.hexToRgba(player1Color, 0.18);
    this.player2ColorBg = this.hexToRgba(player2Color, 0.18);
    this.player1ColorBorder = this.hexToRgba(player1Color, 0.45);
    this.player2ColorBorder = this.hexToRgba(player2Color, 0.45);

    // Nombres cortos
    this.player1ShortName = this.getShortName(this.match.player1?.playerName || 'J1');
    this.player2ShortName = this.getShortName(this.match.player2?.playerName || 'J2');

    // Cargar juegos desde gamesDetailed si existen
    const games = this.match.gamesDetailed && this.match.gamesDetailed.length > 0
      ? this.match.gamesDetailed
      : [];

    // Calcular timestamps si no existen
    const matchStartTs = this.match.matchStartedAt
      ? this.match.matchStartedAt.toMillis()
      : null;

    const matchEndTs = this.match.matchEndedAt
      ? this.match.matchEndedAt.toMillis()
      : null;

    // Configurar estado local para mostrar partido finalizado
    this.localState = {
      currentGame: games.length > 0 ? games.length - 1 : 0,
      games: games.length > 0 ? games : [this.createEmptyGame(1)],
      server: null,
      matchStartTs: matchStartTs,
      gameStartTs: null,
      warmupStartTs: null,
      restStartTs: null,
      timeoutStartTs: null,
      phase: 'FINISHED',
      warmupSeconds: this.match.warmupSeconds || 240,
      restSeconds: this.match.restBetweenGamesSeconds || 120,
      bestOf: this.match.bestOf || 3,
      pointsToWin: this.match.pointsToWin || 11,
      winByTwo: true,
      player1Color: player1Color,
      player2Color: player2Color
    };

    this.currentGameDetail = this.localState.games[this.localState.currentGame];

    // Calcular tiempo transcurrido
    if (matchStartTs && matchEndTs) {
      const durationSeconds = Math.floor((matchEndTs - matchStartTs) / 1000);
      this.matchElapsedTime = this.formatTime(durationSeconds);
    }

    // Marcar como guardado exitosamente
    this.matchSavedSuccessfully = true;
  }

  private initializeLocalState(): void {
    if (!this.match) return;

    // Colores por defecto basados en la línea de diseño de Bolar
    const player1Color = '#14b8a6'; // Teal (cyan)
    const player2Color = '#FF8C42'; // Naranja (Bolar)

    // Calcular colores derivados
    this.player1ColorBg = this.hexToRgba(player1Color, 0.18);
    this.player2ColorBg = this.hexToRgba(player2Color, 0.18);
    this.player1ColorBorder = this.hexToRgba(player1Color, 0.45);
    this.player2ColorBorder = this.hexToRgba(player2Color, 0.45);

    // Nombres cortos
    this.player1ShortName = this.getShortName(this.match.player1?.playerName || 'J1');
    this.player2ShortName = this.getShortName(this.match.player2?.playerName || 'J2');

    // Inicializar estado local
    this.localState = {
      currentGame: 0,
      games: [this.createEmptyGame(1)],
      server: null,
      matchStartTs: null,
      gameStartTs: null,
      warmupStartTs: null,
      restStartTs: null,
      timeoutStartTs: null,
      phase: 'WARMUP',
      warmupSeconds: this.match.warmupSeconds || 240,
      restSeconds: this.match.restBetweenGamesSeconds || 120,
      bestOf: this.match.bestOf || 3,
      pointsToWin: this.match.pointsToWin || 11,
      winByTwo: true,
      player1Color: player1Color,
      player2Color: player2Color
    };

    this.currentGameDetail = this.localState.games[this.localState.currentGame];
  }

  private createEmptyGame(gameNumber: number): GameDetail {
    return {
      number: gameNumber,
      player1Points: 0,
      player2Points: 0,
      events: [],
      totalRallies: 0,
      player1Lets: 0,
      player2Lets: 0,
      player1Strokes: 0,
      player2Strokes: 0,
      startedAt: Timestamp.now()
    };
  }

  // ==================== TIMERS ====================

  private startTimers(): void {
    this.timerInterval = setInterval(() => {
      this.updateTimers();
    }, 1000);
  }

  private updateTimers(): void {
    const now = Date.now();

    // Match timer (total)
    if (this.localState.matchStartTs) {
      const elapsed = Math.floor((now - this.localState.matchStartTs) / 1000);
      this.matchElapsedTime = this.formatTime(elapsed);
    }

    // Game timer
    if (this.localState.gameStartTs && this.localState.phase === 'PLAYING') {
      const elapsed = Math.floor((now - this.localState.gameStartTs) / 1000);
      this.gameElapsedTime = this.formatTime(elapsed);
    }

    // Warmup countdown
    if (this.localState.phase === 'WARMUP' && this.localState.warmupStartTs) {
      const elapsed = Math.floor((now - this.localState.warmupStartTs) / 1000);
      const remaining = this.localState.warmupSeconds - elapsed;
      this.warmupProgress = (elapsed / this.localState.warmupSeconds) * 100;

      if (remaining <= 0) {
        this.showNotification('¡Calentamiento terminado! Selecciona quién sirve primero');
      }
    }

    // Rest countdown
    if (this.localState.phase === 'REST' && this.localState.restStartTs) {
      const elapsed = Math.floor((now - this.localState.restStartTs) / 1000);
      const remaining = this.localState.restSeconds - elapsed;
      this.restProgress = (elapsed / this.localState.restSeconds) * 100;

      if (remaining <= 0) {
        this.showNotification('¡Descanso terminado! Puedes iniciar el siguiente juego');
      }
    }

    // Timeout countup
    if (this.localState.phase === 'TIMEOUT' && this.localState.timeoutStartTs) {
      const elapsed = Math.floor((now - this.localState.timeoutStartTs) / 1000);
      this.timeoutElapsedTime = this.formatTime(elapsed);
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  // ==================== WARMUP ====================

  startWarmup(): void {
    if (this.localState.phase !== 'WARMUP') return;
    this.localState.warmupStartTs = Date.now();
    this.warmupProgress = 0;

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    this.showNotification('¡Calentamiento iniciado!');
  }

  selectServer(player: 1 | 2): void {
    if (this.localState.phase !== 'WARMUP') return;

    this.localState.server = player;
    this.localState.phase = 'PLAYING';
    this.localState.matchStartTs = Date.now();
    this.localState.gameStartTs = Date.now();
    this.localState.warmupStartTs = null;

    const game = this.currentGameDetail!;
    game.startedAt = Timestamp.now();

    // Actualizar estado del partido a JUGANDO en Firestore
    if (this.match?.id) {
      this.service.finalizeMatchWithDetails(this.match.id, {
        state: MatchState.JUGANDO,
        matchStartedAt: Timestamp.fromMillis(this.localState.matchStartTs)
      }).subscribe({
        error: (err) => console.error('Error actualizando estado:', err)
      });
    }

    // Crear evento inicial SERVE_START con "0R"
    const initialEvent: RefereeEvent = {
      timestamp: Timestamp.now(),
      gameNumber: game.number,
      type: 'SERVE_START',
      serverAfter: player,
      serveBoxAfter: 'R',
      scoreAfter: {
        player1: 0,
        player2: 0
      }
    };
    game.events.push(initialEvent);

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    // Sincronizar al inicio del juego para que el tablero vea games:[{0-0}]
    this.syncLiveScore();

    this.showNotification(`${player === 1 ? this.player1ShortName : this.player2ShortName} sirve primero`);
  }

  randomServer(): void {
    // Mostrar animación de sorteo
    this.showingRandomAnimation = true;
    this.randomAnimationResult = null; // No revelar el resultado aún

    // Seleccionar jugador aleatoriamente (pero no mostrarlo aún)
    const randomPlayer = Math.random() < 0.5 ? 1 : 2;

    // Revelar el resultado cuando comience la caída (después de 2.5s de giro)
    setTimeout(() => {
      this.randomAnimationResult = randomPlayer as 1 | 2;
    }, 2500);

    // Cerrar la animación y continuar después de mostrar el resultado (5.5s total)
    // 2.5s giro + 0.5s caída + 2.5s mostrando ganador
    setTimeout(() => {
      this.showingRandomAnimation = false;
      this.selectServer(randomPlayer as 1 | 2);
      this.randomAnimationResult = null;
    }, 5500);
  }

  // ==================== SCORING ====================

  /**
   * Registra un punto para el jugador especificado.
   *
   * Lógica de serve box (lado de saque):
   * - Cambio de servidor: siempre comienza desde R (derecha)
   * - Mismo servidor: alterna entre R y L (R→L→R→L...)
   *
   * @param player Jugador que gana el punto (1 o 2)
   * @param eventType Tipo de evento (POINT, STROKE, NO_LET)
   */
  pointTo(player: 1 | 2, eventType: EventType = 'POINT'): void {
    if (this.localState.phase !== 'PLAYING') return;
    if (!this.localState.server) return;

    // Bloquear acciones mientras se espera confirmación de fin de juego
    if (this.isWaitingForGameEndConfirmation) {
      return;
    }

    const game = this.currentGameDetail!;
    const previousServer = this.localState.server;

    // CALCULAR el lado del saque ACTUAL (antes de incrementar puntos)
    const currentServeBox = this.getCurrentServeBox();

    // Incrementar puntos
    if (player === 1) {
      game.player1Points++;
    } else {
      game.player2Points++;
    }

    // PAR: quien gana sirve
    this.localState.server = player;

    // Calcular lado del saque para el próximo punto con LÓGICA SIMPLE:
    // - Si CAMBIA el servidor → siempre R
    // - Si es el MISMO servidor → alternar (R→L, L→R)
    let nextServeBox: ServeBox;
    if (previousServer !== player) {
      // Cambio de servidor: siempre empieza desde R
      nextServeBox = 'R';
    } else {
      // Mismo servidor: alternar el lado del saque
      nextServeBox = currentServeBox === 'R' ? 'L' : 'R';
    }

    // Registrar evento
    const event: RefereeEvent = {
      timestamp: Timestamp.now(),
      gameNumber: game.number,
      type: eventType,
      pointTo: player,
      serveBox: currentServeBox,         // Lado del saque DURANTE este rally
      serverAfter: this.localState.server,
      serveBoxAfter: nextServeBox,       // Lado del saque para el PRÓXIMO rally
      scoreAfter: {
        player1: game.player1Points,
        player2: game.player2Points
      }
    };
    game.events.push(event);

    // Incrementar rallies si es punto normal
    if (eventType === 'POINT' || eventType === 'STROKE' || eventType === 'NO_LET') {
      game.totalRallies++;
    }

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    // Sincronizar marcador en vivo a Firestore (no bloquea el rally)
    this.syncLiveScore();

    // Verificar ganador del juego
    const winner = this.checkGameWinner(game.player1Points, game.player2Points);

    if (winner) {
      this.handleGameEnd(winner);
    }
  }

  /**
   * Sincroniza el marcador actual del partido a Firestore para que el
   * tablero público vea el score en vivo. Fire-and-forget: si falla,
   * no interrumpe el arbitraje.
   */
  private syncLiveScore(): void {
    if (!this.match?.id) return;

    const games: Game[] = this.localState.games.map(g => ({
      number: g.number,
      player1Points: g.player1Points,
      player2Points: g.player2Points,
    }));

    const counts = this.countGamesWon();

    let p1PointsWon = 0;
    let p1PointsLost = 0;
    let p2PointsWon = 0;
    let p2PointsLost = 0;
    for (const g of this.localState.games) {
      p1PointsWon += g.player1Points;
      p1PointsLost += g.player2Points;
      p2PointsWon += g.player2Points;
      p2PointsLost += g.player1Points;
    }

    this.service.updateMatchPartial(this.match.id, {
      games,
      player1GamesWon: counts[1],
      player2GamesWon: counts[2],
      player1GamesLost: counts[2],
      player2GamesLost: counts[1],
      player1PointsWon: p1PointsWon,
      player1PointsLost: p1PointsLost,
      player2PointsWon: p2PointsWon,
      player2PointsLost: p2PointsLost,
    }).subscribe({
      error: (e) => console.warn('Live score sync failed (no bloquea arbitraje):', e),
    });
  }

  /**
   * Registra un Let (repetir rally sin cambio de score ni servidor).
   * @param requestedBy Jugador que solicitó el let (1 o 2) - se guarda para estadísticas
   */
  letCall(requestedBy: 1 | 2): void {
    if (this.localState.phase !== 'PLAYING') return;
    if (!this.localState.server) return;
    if (this.isWaitingForGameEndConfirmation) return;

    const game = this.currentGameDetail!;
    const currentServeBox = this.getCurrentServeBox();

    const event: RefereeEvent = {
      timestamp: Timestamp.now(),
      gameNumber: game.number,
      type: 'LET',
      pointTo: requestedBy,
      serveBox: currentServeBox,
      serverAfter: this.localState.server,
      serveBoxAfter: currentServeBox,
      scoreAfter: {
        player1: game.player1Points,
        player2: game.player2Points
      }
    };
    game.events.push(event);

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    this.showNotification(`Let solicitado por ${requestedBy === 1 ? this.player1ShortName : this.player2ShortName} - Repetir rally`);
  }

  /**
   * Registra un Stroke (punto otorgado por golpe ilegal o interferencia).
   * @param player Jugador que recibe el punto
   */
  stroke(player: 1 | 2): void {
    this.pointTo(player, 'STROKE');
    this.showNotification(`Stroke para ${player === 1 ? this.player1ShortName : this.player2ShortName}`);
  }

  /**
   * Registra un No-Let (se niega el let, el oponente gana el punto).
   * @param requestedBy Jugador que solicitó el let (1 o 2) - pierde el punto
   */
  noLet(requestedBy: 1 | 2): void {
    if (this.localState.phase !== 'PLAYING') return;
    if (!this.localState.server) return;

    // El que solicita NO LET es el que PIERDE el punto (el oponente gana)
    const opponent: 1 | 2 = requestedBy === 1 ? 2 : 1;

    // Registrar como NO_LET y dar el punto al oponente
    this.pointTo(opponent, 'NO_LET');
    this.showNotification(`No-Let solicitado por ${requestedBy === 1 ? this.player1ShortName : this.player2ShortName} - Punto para ${opponent === 1 ? this.player1ShortName : this.player2ShortName}`);
  }

  /**
   * Verifica si hay un ganador del juego actual según las reglas de squash.
   * - Primer jugador en llegar a pointsToWin puntos gana
   * - EXCEPTO si hay empate (target-1 vs target-1), entonces se requieren 2 puntos de ventaja
   */
  private checkGameWinner(p1: number, p2: number): 1 | 2 | null {
    const target = this.localState.pointsToWin;

    if (p1 >= target && p1 - p2 >= 2) {
      return 1;
    }
    if (p2 >= target && p2 - p1 >= 2) {
      return 2;
    }

    return null;
  }

  /**
   * Maneja el fin de un juego mostrando un diálogo de confirmación.
   * Bloquea las acciones de scoring hasta que el usuario confirme o deshaga.
   */
  private handleGameEnd(winner: 1 | 2): void {
    const game = this.currentGameDetail!;

    // Bloquear acciones mientras se espera confirmación
    this.isWaitingForGameEndConfirmation = true;

    // Abrir diálogo de confirmación ANTES de finalizar el juego
    const dialogRef = this.dialog.open(GameEndConfirmationDialogComponent, {
      data: {
        gameNumber: game.number,
        winner: winner,
        player1Name: this.player1ShortName,
        player2Name: this.player2ShortName,
        player1Score: game.player1Points,
        player2Score: game.player2Points,
        isDarkTheme: this.isDarkTheme
      },
      disableClose: true,
      width: '450px',
      panelClass: 'game-end-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      // Desbloquear acciones independientemente del resultado
      this.isWaitingForGameEndConfirmation = false;

      if (result?.confirmed) {
        this.confirmGameEnd(winner, game);
      } else if (result?.undo) {
        this.undo();
        this.showNotification('Último punto deshecho - juego continúa');
      }
    });
  }

  private confirmGameEnd(winner: 1 | 2, game: GameDetail): void {
    // Marcar el juego como finalizado
    game.winner = winner;
    game.endedAt = Timestamp.now();

    if (game.startedAt && game.endedAt) {
      game.durationSeconds = game.endedAt.seconds - game.startedAt.seconds;
    }

    // Sincronizar (ahora el conteo de juegos ganados incluye este juego)
    this.syncLiveScore();

    // Verificar ganador del partido
    const gamesWon = this.countGamesWon();
    const requiredWins = Math.ceil(this.localState.bestOf / 2);

    if (gamesWon[winner] >= requiredWins) {
      this.handleMatchEnd(winner);
    } else {
      // Iniciar descanso
      this.localState.phase = 'REST';
      this.localState.restStartTs = Date.now();
      this.localState.server = winner;
      this.showNotification(`Juego ${game.number} finalizado. ${winner === 1 ? this.player1ShortName : this.player2ShortName} gana ${game.player1Points}-${game.player2Points}`);
    }
  }

  countGamesWon(): { 1: number; 2: number } {
    const result = { 1: 0, 2: 0 };
    for (const game of this.localState.games) {
      if (game.winner === 1) result[1]++;
      if (game.winner === 2) result[2]++;
    }
    return result;
  }

  /**
   * Inicia el siguiente juego después del descanso.
   * Crea un evento SERVE_START inicial con "0R" para que el rally history muestre el estado inicial.
   * El servidor ya está establecido (es el ganador del juego anterior).
   */
  startNextGame(): void {
    if (this.localState.phase !== 'REST') return;

    const nextGameNumber = this.localState.currentGame + 2;
    const newGame = this.createEmptyGame(nextGameNumber);
    this.localState.games.push(newGame);
    this.localState.currentGame++;
    this.localState.phase = 'PLAYING';
    this.localState.gameStartTs = Date.now();
    this.localState.restStartTs = null;

    this.currentGameDetail = newGame;

    // Crear evento inicial SERVE_START con "0R"
    const initialEvent: RefereeEvent = {
      timestamp: Timestamp.now(),
      gameNumber: newGame.number,
      type: 'SERVE_START',
      serverAfter: this.localState.server!,
      serveBoxAfter: 'R',
      scoreAfter: {
        player1: 0,
        player2: 0
      }
    };
    newGame.events.push(initialEvent);

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    // Sincronizar para que el tablero refleje el nuevo juego de inmediato
    this.syncLiveScore();
  }

  private handleMatchEnd(winner: 1 | 2): void {
    this.localState.phase = 'FINISHED';

    // Detener temporizadores
    this.clearIntervals();

    // Liberar wake lock: el partido terminó, ya no se necesita mantener la pantalla activa
    this.releaseWakeLock();

    const matchEndTs = Date.now();
    const matchDurationSeconds = this.localState.matchStartTs
      ? Math.floor((matchEndTs - this.localState.matchStartTs) / 1000)
      : 0;

    // Guardar datos para posible reintento
    this.pendingFinalizeData = { winner, durationSeconds: matchDurationSeconds };

    this.showNotification(`¡Partido finalizado! Ganador: ${winner === 1 ? this.player1ShortName : this.player2ShortName}`);

    setTimeout(() => {
      this.finalizeMatch(winner, matchDurationSeconds);
    }, 2000);
  }

  retrySyncMatch(): void {
    if (!this.pendingFinalizeData) return;
    this.syncError = false;
    this.finalizeMatch(this.pendingFinalizeData.winner, this.pendingFinalizeData.durationSeconds);
  }

  private finalizeMatch(winner: 1 | 2, durationSeconds: number): void {
    if (!this.match?.id) return;
    this.isSyncing = true;

    // Calcular estadísticas para compatibilidad con sistema anterior
    let player1GamesWon = 0;
    let player1GamesLost = 0;
    let player1PointsWon = 0;
    let player1PointsLost = 0;
    let player2GamesWon = 0;
    let player2GamesLost = 0;
    let player2PointsWon = 0;
    let player2PointsLost = 0;

    // Convertir GameDetail[] a Game[] y calcular estadísticas
    const games = this.localState.games.map(gameDetail => {
      // Sumar puntos totales
      player1PointsWon += gameDetail.player1Points;
      player1PointsLost += gameDetail.player2Points;
      player2PointsWon += gameDetail.player2Points;
      player2PointsLost += gameDetail.player1Points;

      // Contar juegos ganados/perdidos
      if (gameDetail.winner === 1) {
        player1GamesWon++;
        player2GamesLost++;
      } else if (gameDetail.winner === 2) {
        player2GamesWon++;
        player1GamesLost++;
      }

      // Retornar en formato Game (sin eventos)
      return {
        number: gameDetail.number,
        player1Points: gameDetail.player1Points,
        player2Points: gameDetail.player2Points
      };
    });

    // Preparar datos para Firestore
    const matchData = {
      state: MatchState.FINALIZADO,
      playerIdWinner: winner === 1 ? this.match.player1Id : this.match.player2Id,
      matchStartedAt: this.localState.matchStartTs ? Timestamp.fromMillis(this.localState.matchStartTs) : undefined,
      matchEndedAt: Timestamp.now(),
      matchDurationSeconds: durationSeconds,
      player1GamesWon,
      player1GamesLost,
      player1PointsWon,
      player1PointsLost,
      player2GamesWon,
      player2GamesLost,
      player2PointsWon,
      player2PointsLost,
      games,
      gamesDetailed: this.localState.games
    };

    this.service.finalizeMatchWithDetails(this.match.id, matchData).pipe(
      timeout(10000) // 10 segundos: Firestore no falla en offline, queda pendiente
    ).subscribe({
      next: () => {
        this.isSyncing = false;
        this.syncError = false;
        this.matchSavedSuccessfully = true;
        this.pendingFinalizeData = null;
        this.showNotification('Partido guardado exitosamente');

        // Limpiar estado de localStorage ya que el partido finalizó
        if (this.match?.code) {
          this.clearStateFromLocalStorage(this.match.code);
        }
      },
      error: (err) => {
        this.isSyncing = false;
        this.syncError = true;
        if (err instanceof TimeoutError) {
          console.warn('Timeout al guardar partido - sin conexión');
        } else {
          console.error('Error finalizando partido:', err);
        }
        this.showError('Sin conexión - usa el botón para sincronizar cuando tengas red');
      }
    });
  }

  // ==================== UNDO ====================

  undo(): void {
    const game = this.currentGameDetail!;

    if (game.events.length === 0) {
      if (this.localState.currentGame > 0) {
        // Eliminar juego actual y volver al anterior
        this.localState.games.pop();
        this.localState.currentGame--;
        this.currentGameDetail = this.localState.games[this.localState.currentGame];
        this.showNotification('Juego anterior restaurado');
      } else {
        this.showNotification('No hay eventos para deshacer');
      }
      return;
    }

    // Proteger el evento inicial SERVE_START (0R)
    // Solo debe haber 1 evento y debe ser de tipo SERVE_START para bloquearlo
    if (game.events.length === 1 && game.events[0].type === 'SERVE_START') {
      this.showNotification('No se puede deshacer el evento inicial del juego');
      return;
    }

    // Eliminar último evento
    game.events.pop();

    // Reconstruir estado desde eventos
    this.rebuildGameFromEvents(game);

    // Guardar estado en localStorage
    if (this.match?.code) {
      this.saveStateToLocalStorage(this.match.code);
    }

    // Sincronizar marcador en vivo tras el undo
    this.syncLiveScore();

    this.showNotification('Última acción deshecha');
  }

  private rebuildGameFromEvents(game: GameDetail): void {
    // Resetear counters
    game.player1Points = 0;
    game.player2Points = 0;
    game.totalRallies = 0;
    game.player1Lets = 0;
    game.player2Lets = 0;
    game.player1Strokes = 0;
    game.player2Strokes = 0;

    // Reproducir todos los eventos
    for (const event of game.events) {
      switch (event.type) {
        case 'POINT':
        case 'STROKE':
        case 'NO_LET':
          if (event.pointTo === 1) game.player1Points++;
          if (event.pointTo === 2) game.player2Points++;
          game.totalRallies++;
          break;

        case 'LET':
          if (event.pointTo === 1) game.player1Lets++;
          if (event.pointTo === 2) game.player2Lets++;
          break;
      }

      // Actualizar servidor
      this.localState.server = event.serverAfter;
    }
  }

  // ==================== HELPERS ====================

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private getShortName(name: string): string {
    return name.trim().split(' ')[0];
  }

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private showNotification(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  private clearIntervals(): void {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    this.cdr.detectChanges();
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  getGamesWonDisplay(): string {
    const gamesWon = this.countGamesWon();
    return `${gamesWon[1]} - ${gamesWon[2]}`;
  }

  /**
   * Obtiene el historial de rallies del juego actual para mostrar sobre la línea naranja.
   * TODOS los badges muestran el ESTADO ANTES del rally: score + lado desde donde SE SACARÁ.
   * Ejemplo: "1L" = Score 1, se sacará desde L en el próximo rally.
   * Retorna en orden inverso (más reciente primero).
   */
  getRallyHistory(): Array<{ displayText: string; score: string; serveBox: ServeBox; serverPlayer: 1 | 2; eventType: 'point' | 'let' | 'stroke' | 'nolet' }> {
    const game = this.currentGameDetail;
    if (!game || game.events.length === 0) return [];

    const rallies: Array<{ displayText: string; score: string; serveBox: ServeBox; serverPlayer: 1 | 2; eventType: 'point' | 'let' | 'stroke' | 'nolet' }> = [];

    for (let i = 0; i < game.events.length; i++) {
      const event = game.events[i];

      if (event.type === 'SERVE_START') {
        // Estado inicial: "0R" - usa serveBoxAfter porque aún no se ha sacado
        const displayBox = event.serveBoxAfter || 'R';
        rallies.push({
          displayText: `0${displayBox}`,
          score: '0-0',
          serveBox: displayBox,
          serverPlayer: event.serverAfter,
          eventType: 'point'
        });
      } else if (event.type === 'POINT') {
        // Puntos normales: mostrar score + lado desde donde SE SACARÁ
        const playerPointNumber = event.pointTo === 1
          ? event.scoreAfter.player1
          : event.scoreAfter.player2;

        // Usar serveBoxAfter (lado desde donde SE SACARÁ en el próximo rally)
        const displayBox = event.serveBoxAfter || 'R';

        rallies.push({
          displayText: `${playerPointNumber}${displayBox}`,
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: event.serverAfter,
          eventType: 'point'
        });
      } else if (event.type === 'STROKE') {
        // STROKE: mostrar DOS badges:
        // 1. "ST" del jugador que lo solicitó y ganó
        // 2. El punto que se sumó (score + lado futuro)

        const displayBox = event.serveBoxAfter || 'R';
        const playerPointNumber = event.pointTo === 1
          ? event.scoreAfter.player1
          : event.scoreAfter.player2;

        // Badge 1: Evento ST
        rallies.push({
          displayText: 'ST',
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: event.pointTo!, // El que ganó el stroke
          eventType: 'stroke'
        });

        // Badge 2: Punto sumado con lado futuro
        rallies.push({
          displayText: `${playerPointNumber}${displayBox}`,
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: event.serverAfter,
          eventType: 'point'
        });
      } else if (event.type === 'NO_LET') {
        // NO_LET: mostrar DOS badges:
        // 1. "NL" del jugador que lo solicitó (el que perdió)
        // 2. El punto que ganó el oponente (score + lado futuro)

        const requestedBy: 1 | 2 = event.pointTo === 1 ? 2 : 1;
        const displayBox = event.serveBoxAfter || 'R';
        const playerPointNumber = event.pointTo === 1
          ? event.scoreAfter.player1
          : event.scoreAfter.player2;

        // Badge 1: Evento NL del que lo solicitó
        rallies.push({
          displayText: 'NL',
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: requestedBy, // El que solicitó el let
          eventType: 'nolet'
        });

        // Badge 2: Punto sumado con lado futuro
        rallies.push({
          displayText: `${playerPointNumber}${displayBox}`,
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: event.serverAfter,
          eventType: 'point'
        });
      } else if (event.type === 'LET') {
        // Let: mostrar "YL" del jugador que lo solicitó
        const displayBox = event.serveBoxAfter || 'R';
        rallies.push({
          displayText: 'YL',
          score: `${event.scoreAfter.player1}-${event.scoreAfter.player2}`,
          serveBox: displayBox,
          serverPlayer: event.pointTo || event.serverAfter, // Quién lo solicitó (para ubicar el badge)
          eventType: 'let'
        });
      }
    }

    // Retornar en orden inverso (más reciente primero)
    return rallies.reverse();
  }

  getGameHistory(): string[] {
    return this.localState.games
      .filter(g => g.winner)
      .map(g => `J${g.number}: ${g.player1Points}-${g.player2Points}`);
  }

  getWarmupTimeRemaining(): number {
    if (!this.localState.warmupStartTs) return this.localState.warmupSeconds;
    const elapsed = Math.floor((Date.now() - this.localState.warmupStartTs) / 1000);
    return Math.max(0, this.localState.warmupSeconds - elapsed);
  }

  getRestTimeRemaining(): number {
    if (!this.localState.restStartTs) return 0;
    const elapsed = Math.floor((Date.now() - this.localState.restStartTs) / 1000);
    return Math.max(0, this.localState.restSeconds - elapsed);
  }

  // ==================== SERVE BOX LOGIC ====================

  /**
   * Obtiene el lado de saque actual desde el último evento.
   * Este es el lado que se USARÁ en el próximo rally.
   */
  getCurrentServeBox(): ServeBox {
    const game = this.currentGameDetail;

    // Si no hay eventos (juego recién comenzado), usar 'R' por defecto
    if (!game || game.events.length === 0) {
      return 'R';
    }

    // Obtener el último evento y retornar el lado del próximo saque
    const lastEvent = game.events[game.events.length - 1];
    return lastEvent.serveBoxAfter || 'R';
  }

  /**
   * Permite cambiar el lado de saque manualmente.
   * Actualiza el serveBoxAfter del último evento.
   */
  changeServeBox(box: ServeBox): void {
    const game = this.currentGameDetail;
    if (game && game.events.length > 0) {
      const lastEvent = game.events[game.events.length - 1];
      lastEvent.serveBoxAfter = box;
        this.showNotification(`Lado de saque cambiado a ${box === 'R' ? 'Derecha' : 'Izquierda'}`);
    }
  }

  /**
   * Abre el diálogo de selección de color para un jugador.
   * Actualiza el color en el estado local y en Firestore.
   */
  openColorPicker(playerNumber: 1 | 2): void {
    if (!this.match || !this.currentGameDetail) return;

    const currentColor = playerNumber === 1
      ? this.localState.player1Color
      : this.localState.player2Color;

    const dialogRef = this.dialog.open(PlayerColorPickerDialogComponent, {
      data: {
        playerNumber,
        currentColor,
        isDarkTheme: this.isDarkTheme
      },
      width: '400px',
      maxWidth: '95vw',
      panelClass: 'color-picker-dialog-panel',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.color && result.color !== currentColor) {
        this.updatePlayerColor(playerNumber, result.color);
      }
    });
  }

  /**
   * Actualiza el color de un jugador en el estado local.
   */
  private updatePlayerColor(playerNumber: 1 | 2, newColor: string): void {
    // Actualizar estado local
    if (playerNumber === 1) {
      this.localState.player1Color = newColor;
      this.player1ColorBg = this.hexToRgba(newColor, 0.18);
      this.player1ColorBorder = this.hexToRgba(newColor, 0.45);
    } else {
      this.localState.player2Color = newColor;
      this.player2ColorBg = this.hexToRgba(newColor, 0.18);
      this.player2ColorBorder = this.hexToRgba(newColor, 0.45);
    }

    this.showNotification(`Color de ${playerNumber === 1 ? this.player1ShortName : this.player2ShortName} actualizado`);
  }

  /**
   * Retorna el estado visual para un lado específico (R o L)
   * - 'filled': Lado activo (con relleno del color del jugador)
   * - 'outline': Lado inactivo pero siempre seleccionable (solo borde)
   */
  getServeBoxState(box: ServeBox): 'filled' | 'outline' | 'disabled' {
    const currentBox = this.getCurrentServeBox();

    if (currentBox === box) {
      return 'filled'; // Lado activo
    }

    return 'outline'; // Lado inactivo pero siempre seleccionable
  }

  /**
   * Retorna los estilos dinámicos para un botón de serve box.
   */
  getServeBoxStyles(player: 1 | 2, state: 'filled' | 'outline' | 'disabled'): any {
    const color = player === 1 ? this.localState.player1Color : this.localState.player2Color;

    if (state === 'filled') {
      const luminance = this.calculateLuminance(color);
      const textColor = luminance > 0.5 ? '#000000' : '#ffffff';

      return {
        backgroundColor: color,
        borderColor: color,
        color: textColor,
        boxShadow: `0 3px 8px ${this.hexToRgba(color, 0.5)}, 0 0 0 2px ${this.hexToRgba(color, 0.2)}`
      };
    } else if (state === 'outline') {
      return {
        backgroundColor: 'transparent',
        borderColor: color,
        color: color,
        boxShadow: `0 0 0 2px ${this.hexToRgba(color, 0.15)}`
      };
    }

    return {};
  }

  /**
   * Retorna el color de fondo para un rally badge.
   */
  getRallyBadgeColor(rally: { serverPlayer: 1 | 2; eventType: 'point' | 'let' | 'stroke' | 'nolet' }): string {
    // Eventos especiales tienen colores fijos
    if (rally.eventType === 'let') return '#fbbf24';
    if (rally.eventType === 'stroke') return '#10b981';
    if (rally.eventType === 'nolet') return '#ef4444';

    // Puntos normales usan el color del jugador
    return rally.serverPlayer === 1 ? this.localState.player1Color : this.localState.player2Color;
  }

  /**
   * Calcula la luminancia relativa de un color hexadecimal.
   * Retorna un valor entre 0 (negro) y 1 (blanco).
   */
  private calculateLuminance(hex: string): number {
    // Convertir hex a RGB
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    // Aplicar corrección gamma (sRGB)
    const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

    // Calcular luminancia usando coeficientes estándar ITU-R BT.709
    return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
  }

  /**
   * Retorna el color de texto apropiado (blanco o negro) basado en la luminancia del fondo.
   * Usa un umbral de 0.5 para garantizar buen contraste.
   */
  getRallyBadgeTextColor(rally: { serverPlayer: 1 | 2; eventType: 'point' | 'let' | 'stroke' | 'nolet' }): string {
    const bgColor = this.getRallyBadgeColor(rally);
    const luminance = this.calculateLuminance(bgColor);

    // Si la luminancia es mayor a 0.5, el fondo es claro → usar texto negro
    // Si la luminancia es menor o igual a 0.5, el fondo es oscuro → usar texto blanco
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  /**
   * Retorna el color de borde para un botón de acción.
   */
  getActionButtonBorderColor(player: 1 | 2): string {
    return player === 1 ? this.localState.player1Color : this.localState.player2Color;
  }
}
